import React from "react";
import { HbrCard, HbrTag } from "@harbor/elements-react-standalone";
import { useMoodleCourseCompletion } from '../../../../hooks/useMoodleHooks';
import StbLoader from '../../../security-toolbox-ui/StbLoader';

function percent(val, total) {
  if (!total) return "0%";
  return `${Math.round((val / total) * 100)}%`;
}
function formatDate(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toLocaleString();
  } catch {
    return "—";
  }
}

export function CourseCompletion({ courseId, groupId }) {
  const { data, isLoading, error } = useMoodleCourseCompletion(courseId, { groupId });

  if (!courseId || !groupId) return <div>No course or group selected.</div>;
  if (isLoading) return <StbLoader isLoading={isLoading} />;
  if (error) return <div style={{ color: "red" }}>{error.message || String(error)}</div>;
  if (!Array.isArray(data) || !data.length)
    return <div>No completion data found.</div>;

  // --- Calculate course-level metrics ---
  const userMetrics = data.map((entry) => {
    const { activities_completion } = entry;
    const statuses = activities_completion?.statuses || [];
    const completed = statuses.filter((s) => s.isoverallcomplete).length;
    const total = statuses.length;
    return {
      completed,
      total,
      percent: total > 0 ? completed / total : 0,
      isAllComplete: total > 0 && completed === total,
      isNoneComplete: completed === 0,
    };
  });
  const totalUsers = data.length;
  const avgCompletion = Math.round(
    userMetrics.reduce((sum, u) => sum + u.percent, 0) / totalUsers * 100
  );
  const fullyComplete = userMetrics.filter(u => u.isAllComplete).length;
  const noneComplete = userMetrics.filter(u => u.isNoneComplete).length;
  const inProgress = totalUsers - fullyComplete - noneComplete;

  return (
    <div className="hbr-css__container-section">
      <h3 style={{ marginBottom: 12 }}>
        Course Completion Overview ({totalUsers} users)
      </h3>
      <div className="hbr-css__layout-row-md">
        <HbrCard style={{
          padding: "1.2em 2em",
          flex: "1 1 180px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#f4f9ff"
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#1D69CC" }}>{avgCompletion}%</div>
          <div style={{ fontSize: 13, color: "#555" }}>Avg. Completion</div>
        </HbrCard>
        <HbrCard style={{
          padding: "1.2em 2em",
          flex: "1 1 180px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#e3fbe3"
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#45991F" }}>{fullyComplete}</div>
          <div style={{ fontSize: 13, color: "#555" }}>Fully Completed</div>
        </HbrCard>
        <HbrCard style={{
          padding: "1.2em 2em",
          flex: "1 1 180px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#fffbe3"
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#BD7202" }}>{inProgress}</div>
          <div style={{ fontSize: 13, color: "#555" }}>In Progress</div>
        </HbrCard>
        <HbrCard style={{
          padding: "1.2em 2em",
          flex: "1 1 180px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          background: "#fbe3e3"
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "#CC2D37" }}>{noneComplete}</div>
          <div style={{ fontSize: 13, color: "#555" }}>Not Started</div>
        </HbrCard>
      </div>
      <div className="hbr-css__layout-row-md">
      {/* User-level breakdown */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "28px",
          marginTop: "8px",
          width: "100%"
        }}
      >
        {data.map((entry) => {
          const { user, activities_completion } = entry;
          const statuses = activities_completion?.statuses || [];
          const completed = statuses.filter((s) => s.isoverallcomplete).length;
          const total = statuses.length;

          // Participation details
          const lastAccess = formatDate(user.lastaccess || user.lastcourseaccess);
          const firstAccess = formatDate(user.firstaccess);
          const city = user.city || "—";
          const country = user.country || "—";
          const department = user.department || "—";
          const orgField = Array.isArray(user.customfields)
            ? user.customfields.find(f => (f.shortname || "").toLowerCase() === "organization")
            : null;

          return (
            <HbrCard
              key={user.id}
              className="hbr-css__container-card"
              style={{
                padding: 22,
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                boxSizing: "border-box"
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 18 }}>
                <img
                  src={user.profileimageurl || user.profileimageurlsmall}
                  alt={user.fullname}
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "2px solid #eee",
                  }}
                />
                <div>
                  <div style={{ fontWeight: 700, fontSize: "1.09em" }}>{user.fullname}</div>
                  <div style={{ fontSize: ".94em", color: "#666" }}>{user.email}</div>
                  {orgField && (
                    <div style={{ fontSize: "0.89em", color: "#3a4b66", marginTop: 3 }}>
                      <HbrTag size="small">{orgField.value}</HbrTag>
                    </div>
                  )}
                </div>
              </div>
              <div>
                {/* Progress bar */}
                <div style={{ fontWeight: 600, fontSize: "1.09em", marginBottom: 7 }}>
                  {completed} / {total} activities complete
                </div>
                <div style={{
                  width: "100%",
                  height: 14,
                  background: "#eee",
                  borderRadius: 9,
                  marginTop: 2,
                  marginBottom: 10,
                  position: "relative"
                }}>
                  <div
                    style={{
                      position: "absolute", left: 0, top: 0, bottom: 0,
                      width: percent(completed, total),
                      background: "#0a66ff",
                      borderRadius: 9,
                      transition: "width 0.3s"
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      left: "50%",
                      top: 0,
                      fontSize: "0.89em",
                      color: "#222",
                      transform: "translateX(-50%)"
                    }}
                  >
                    {percent(completed, total)}
                  </span>
                </div>
                {/* Participation info */}
                <div style={{ fontSize: ".95em", color: "#5a6473", margin: "10px 0 2px 0" }}>
                  <div>
                    <strong>First Access:</strong> {firstAccess}
                  </div>
                  <div>
                    <strong>Last Access:</strong> {lastAccess}
                  </div>
                  <div>
                    <strong>Department:</strong> {department}
                  </div>
                  <div>
                    <strong>Location:</strong> {city}, {country}
                  </div>
                </div>
              </div>
              <details style={{ marginTop: 16 }}>
                <summary style={{ cursor: "pointer", fontWeight: 500 }}>
                  Show activity breakdown
                </summary>
                <table style={{ width: "100%", marginTop: 10, fontSize: "0.97em", borderCollapse: "collapse" }}>
                  <thead>
                  <tr>
                    <th align="left">Activity</th>
                    <th align="left">Type</th>
                    <th>Status</th>
                    <th>Details</th>
                  </tr>
                  </thead>
                  <tbody>
                  {statuses.map((s) => (
                    <tr key={s.cmid} style={{ background: s.isoverallcomplete ? "#e8f5e9" : "#fff" }}>
                      <td>{s.cmid}</td>
                      <td>{s.modname}</td>
                      <td>
                        {s.isoverallcomplete ? (
                          <span style={{ color: "#2e7d32", fontWeight: 600 }}>Complete</span>
                        ) : (
                          <span style={{ color: "#b23c17", fontWeight: 600 }}>Incomplete</span>
                        )}
                      </td>
                      <td>
                        {s.details?.map((d, i) => (
                          <div key={i} style={{ color: "#555" }}>
                            {d.rulename}: {d.rulevalue?.description}
                          </div>
                        ))}
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </details>
            </HbrCard>
          );
        })}
      </div>
    </div>
    </div>
  );
}